import type { Scene } from 'three';
import {
  AdditiveBlending,
  BoxGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  PointLight,
  RingGeometry,
  Vector3,
} from 'three';

interface Tracer {
  mesh: Mesh;
  life: number;
  maxLife: number;
}

interface Flash {
  mesh: Mesh;
  light: PointLight;
  life: number;
  maxLife: number;
  intensity: number;
}

interface DustRing {
  mesh: Mesh;
  life: number;
  maxLife: number;
  baseScale: number;
}

const scratchDirection = new Vector3();
const scratchMid = new Vector3();
const scratchUpAxis = new Vector3(0, 0, 1);

/**
 * Pooled visual effects: weapon tracers, impact flashes and landing dust
 * rings. Everything is pre-allocated so the hot path does not allocate.
 */
export class Effects {
  private readonly group = new Group();
  private readonly tracers: Tracer[] = [];
  private readonly flashes: Flash[] = [];
  private readonly dust: DustRing[] = [];

  constructor(scene: Scene, tracerCount = 24, flashCount = 12, dustCount = 8) {
    const tracerGeometry = new BoxGeometry(0.03, 0.03, 1);
    const tracerMaterial = new MeshBasicMaterial({
      color: 0x9ff6ff,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    });

    for (let index = 0; index < tracerCount; index += 1) {
      const mesh = new Mesh(tracerGeometry, tracerMaterial.clone());
      mesh.visible = false;
      this.group.add(mesh);
      this.tracers.push({ mesh, life: 0, maxLife: 0.08 });
    }

    const flashGeometry = new BoxGeometry(0.28, 0.28, 0.28);
    for (let index = 0; index < flashCount; index += 1) {
      const mesh = new Mesh(
        flashGeometry,
        new MeshBasicMaterial({
          color: 0xffc27a,
          transparent: true,
          blending: AdditiveBlending,
          depthWrite: false,
        }),
      );
      mesh.visible = false;
      const light = new PointLight(0xffb27a, 0, 8);
      light.visible = false;
      this.group.add(mesh, light);
      this.flashes.push({ mesh, light, life: 0, maxLife: 0.18, intensity: 2.4 });
    }

    const dustGeometry = new RingGeometry(0.22, 0.3, 24);
    for (let index = 0; index < dustCount; index += 1) {
      const mesh = new Mesh(
        dustGeometry,
        new MeshBasicMaterial({
          color: 0xbfd4d9,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          side: DoubleSide,
        }),
      );
      mesh.visible = false;
      this.group.add(mesh);
      this.dust.push({ mesh, life: 0, maxLife: 0.45, baseScale: 1 });
    }

    scene.add(this.group);
  }

  spawnTracer(from: Vector3, to: Vector3): void {
    const tracer = this.tracers.find((entry) => entry.life <= 0);
    if (!tracer) return;

    scratchDirection.subVectors(to, from);
    const length = scratchDirection.length();
    if (length < 1e-4) return;
    scratchDirection.divideScalar(length);
    scratchMid.copy(from).add(to).multiplyScalar(0.5);

    tracer.mesh.position.copy(scratchMid);
    tracer.mesh.scale.set(1, 1, length);
    tracer.mesh.lookAt(to);
    tracer.mesh.visible = true;
    tracer.life = tracer.maxLife;
  }

  spawnImpact(position: Vector3, color = 0xffc27a, intensity = 2.4, life = 0.18): void {
    const flash = this.flashes.find((entry) => entry.life <= 0);
    if (!flash) return;

    flash.mesh.position.copy(position);
    flash.mesh.visible = true;
    flash.mesh.scale.setScalar(0.4);
    (flash.mesh.material as MeshBasicMaterial).color.setHex(color);
    flash.light.position.copy(position);
    flash.light.intensity = intensity;
    flash.light.visible = true;
    flash.intensity = intensity;
    flash.maxLife = life;
    flash.life = flash.maxLife;
  }

  /** Expanding ring at a foot contact point; strength is 0..1. */
  spawnLandingDust(position: Vector3, up: Vector3, strength = 1): void {
    const ring = this.dust.find((entry) => entry.life <= 0);
    if (!ring) return;

    ring.mesh.position.copy(position);
    ring.mesh.quaternion.setFromUnitVectors(scratchUpAxis, up);
    ring.baseScale = 0.7 + Math.min(1, Math.max(0, strength)) * 0.6;
    ring.mesh.scale.setScalar(ring.baseScale);
    ring.mesh.visible = true;
    const material = ring.mesh.material as MeshBasicMaterial;
    material.opacity = 0.55;
    ring.life = ring.maxLife;
  }

  update(dt: number): void {
    for (const tracer of this.tracers) {
      if (tracer.life <= 0) continue;
      tracer.life -= dt;
      const material = tracer.mesh.material as MeshBasicMaterial;
      material.opacity = Math.max(0, tracer.life / tracer.maxLife);
      if (tracer.life <= 0) tracer.mesh.visible = false;
    }

    for (const flash of this.flashes) {
      if (flash.life <= 0) continue;
      flash.life -= dt;
      const t = Math.max(0, flash.life / flash.maxLife);
      const material = flash.mesh.material as MeshBasicMaterial;
      material.opacity = t;
      flash.mesh.scale.setScalar(0.4 + (1 - t) * 1.4);
      flash.light.intensity = t * flash.intensity;
      if (flash.life <= 0) {
        flash.mesh.visible = false;
        flash.light.visible = false;
      }
    }

    for (const ring of this.dust) {
      if (ring.life <= 0) continue;
      ring.life -= dt;
      const t = Math.max(0, ring.life / ring.maxLife);
      const material = ring.mesh.material as MeshBasicMaterial;
      material.opacity = t * 0.55;
      ring.mesh.scale.setScalar(ring.baseScale + (1 - t) * 1.8);
      if (ring.life <= 0) ring.mesh.visible = false;
    }
  }
}
